import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../helpers/AuthContext";
import apiClient from "../../services/apiClient";
import {
  EMPTY_RESULTS,
  EMPTY_WARNINGS,
  MIN_QUERY_LENGTH,
  RESULT_GROUPS,
  buildGroupedItems,
  getGroupConfig,
  getInstanceSlugFromPath,
  normalizeGlobalSearchResponse,
} from "./globalHeaderSearchUtils";
import "./GlobalHeaderSearch.css";

const SEARCH_TIMEOUT_MS = 8000;

const isEditableTarget = (target) => {
  const tagName = String(target?.tagName || "").toLowerCase();
  return (
    target?.isContentEditable ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select"
  );
};

const isCanceledRequest = (error) =>
  axios.isCancel?.(error) ||
  error?.__CANCEL__ === true ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const GlobalHeaderSearch = () => {
  const { authState } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [warnings, setWarnings] = useState(EMPTY_WARNINGS);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const instanceSlug = getInstanceSlugFromPath(location.pathname || "");
  const trimmedQuery = query.trim();

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    setQuery("");
    setResults(EMPTY_RESULTS);
    setWarnings(EMPTY_WARNINGS);
    setError("");
    setLoading(false);
    setOpen(false);
    setActiveIndex(-1);
  }, [instanceSlug]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  useEffect(() => {
    const focusSearch = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
      if (isEditableTarget(event.target) && event.target !== inputRef.current) return;

      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
      inputRef.current?.select();
    };

    document.addEventListener("keydown", focusSearch);
    return () => document.removeEventListener("keydown", focusSearch);
  }, []);

  useEffect(() => {
    if (
      trimmedQuery.length < MIN_QUERY_LENGTH ||
      !instanceSlug ||
      !authState?.status ||
      !authState?.id
    ) {
      requestIdRef.current += 1;
      setResults(EMPTY_RESULTS);
      setWarnings(EMPTY_WARNINGS);
      setError("");
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    const cancelSource = axios.CancelToken.source();

    const timeout = window.setTimeout(() => {
      const isCurrentRequest = () =>
        isMountedRef.current && requestIdRef.current === currentRequestId;

      setLoading(true);
      setResults(EMPTY_RESULTS);
      setWarnings(EMPTY_WARNINGS);
      setError("");
      setActiveIndex(-1);

      apiClient
        .get("/global-search", {
          cancelToken: cancelSource.token,
          timeout: SEARCH_TIMEOUT_MS,
          params: {
            instanceUniqueId: instanceSlug,
            q: trimmedQuery,
            limitPerType: 5,
          },
        })
        .then((response) => {
          if (!isCurrentRequest()) return;
          const normalized = normalizeGlobalSearchResponse(response);
          setResults(normalized.results);
          setWarnings(normalized.warnings);
        })
        .catch((requestError) => {
          if (!isCurrentRequest() || isCanceledRequest(requestError)) return;
          console.error("Global search failed", requestError);
          setError(
            requestError?.code === "ECONNABORTED"
              ? "Search took too long."
              : "Search is temporarily unavailable."
          );
        })
        .finally(() => {
          if (isCurrentRequest()) {
            setLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      cancelSource.cancel("Global header search request canceled.");
    };
  }, [authState?.id, authState?.status, instanceSlug, trimmedQuery]);

  const items = useMemo(() => buildGroupedItems(results), [results]);

  useEffect(() => {
    setActiveIndex((current) => {
      if (!items.length) return -1;
      return current >= items.length ? items.length - 1 : current;
    });
  }, [items]);

  if (!authState?.status || !instanceSlug) return null;

  const navigateToItem = (item) => {
    const url = item?.record?.url;
    if (!url) return;
    history.push(url);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!items.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (current + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (current <= 0 ? items.length - 1 : current - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      navigateToItem(activeIndex >= 0 ? items[activeIndex] : items[0]);
    }
  };

  const hasResults = items.length > 0;
  const hasWarnings = warnings.length > 0;
  const shouldShowDropdown = open && Boolean(trimmedQuery);
  const activeOptionId =
    activeIndex >= 0 ? `ec-global-search-option-${activeIndex}` : undefined;
  const statusText = loading
    ? hasResults
      ? `${items.length} result${items.length === 1 ? "" : "s"}`
      : "Searching"
    : trimmedQuery.length < MIN_QUERY_LENGTH
    ? "Ctrl K"
    : hasResults
    ? `${items.length} result${items.length === 1 ? "" : "s"}`
    : "No results";

  let flatIndex = -1;

  const renderResult = (groupKey, record) => {
    flatIndex += 1;
    const itemIndex = flatIndex;
    const { icon: GroupIcon } = getGroupConfig(groupKey);
    return (
      <button
        id={`ec-global-search-option-${itemIndex}`}
        type="button"
        role="option"
        aria-selected={activeIndex === itemIndex}
        className={`ec-global-search__result${activeIndex === itemIndex ? " is-active" : ""}`}
        key={record.key}
        onMouseEnter={() => setActiveIndex(itemIndex)}
        onClick={() => navigateToItem({ groupKey, record })}
      >
        <span>
          <strong>
            <GroupIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: "text-top" }} />
            {record.primaryLabel}
          </strong>
          <small>
            {[record.contextLabel, record.secondaryLabel, record.status].filter(Boolean).join(" - ")}
          </small>
        </span>
        <KeyboardReturnIcon />
      </button>
    );
  };

  return (
    <div className="ec-global-search" ref={rootRef}>
      <SearchIcon className="ec-global-search__icon" aria-hidden="true" />
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value.slice(0, 100));
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search Elite Core records"
        aria-label="Search Elite Core records"
        aria-expanded={shouldShowDropdown}
        aria-controls="ec-global-search-results"
        aria-activedescendant={activeOptionId}
        aria-autocomplete="list"
        role="combobox"
      />
      <span className={`ec-global-search__status${loading ? " is-loading" : ""}`}>
        {statusText}
      </span>
      <span className="ec-global-search__sr-status" aria-live="polite">
        {loading
          ? hasResults
            ? `${items.length} results loaded. More results are still loading.`
            : "Searching Elite Core records."
          : `${items.length} search results loaded.`}
      </span>

      {shouldShowDropdown ? (
        <div className="ec-global-search__dropdown" id="ec-global-search-results" role="listbox">
          <div className="ec-global-search__heading">
            <span>Elite Core records</span>
            <span>{loading ? "Searching..." : statusText}</span>
          </div>

          {trimmedQuery.length < MIN_QUERY_LENGTH ? (
            <div className="ec-global-search__message">Enter at least two characters.</div>
          ) : null}

          {error ? <div className="ec-global-search__message is-error">{error}</div> : null}
          {!error && hasWarnings
            ? warnings.map((warning) => (
                <div className="ec-global-search__message is-error" key={warning}>
                  {warning}
                </div>
              ))
            : null}

          {RESULT_GROUPS.map((groupKey) => {
            if (!results[groupKey]?.length) return null;
            const { label, icon: GroupIcon } = getGroupConfig(groupKey);
            return (
              <section className="ec-global-search__group" aria-label={label} key={groupKey}>
                <div className="ec-global-search__group-title">
                  <GroupIcon /> {label}
                </div>
                {results[groupKey].map((record) => renderResult(groupKey, record))}
              </section>
            );
          })}

          {trimmedQuery.length >= MIN_QUERY_LENGTH && !loading && !hasResults && !error ? (
            <div className="ec-global-search__message">
              No matching Elite Core records.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default GlobalHeaderSearch;
